import React, { memo, useCallback } from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { NodeType } from "../../../models/nodeType";
import { useForm } from "react-hook-form";
import {
  Box,
  FormControl,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
} from "@chakra-ui/react";
import BaseNode from "../../../features/reactflow/nodes/base/BaseNode";

interface FormData {
  x: number;
  y: number;
}

const CoordinatesNode: React.FC<NodeProps<NodeType>> = (props) => {
  const {
    id: nodeId,
    data: { color, nodeData = {} },
  } = props;

  const formData = nodeData as FormData | undefined;
  const x = formData?.x;
  const y = formData?.y;

  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: {
      x,
      y,
    },
    mode: "onBlur",
  });

  const reactFlow = useReactFlow();

  const handleUpdate = useCallback(
    ({ x, y }: FormData) => {
      reactFlow.setNodes((nodes) => {
        const node = nodes.find(({ id }) => id === nodeId);

        if (node == null) {
          return nodes;
        }

        const nodeDataCloned = structuredClone(node.data) as NodeType;

        const nodeData = (nodeDataCloned.nodeData as FormData) ?? {};
        nodeData.x = Number(x) || 0;
        nodeData.y = Number(y) || 0;

        node.data = {
          ...nodeDataCloned,
          nodeData,
        };

        return nodes;
      });
    },
    [reactFlow]
  );

  return (
    <BaseNode {...props}>
      <Box p={2} borderWidth={1}>
        <form className={"nodrag"} onSubmit={handleSubmit(handleUpdate)} onBlur={handleSubmit(handleUpdate)}>
          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                X
              </Text>
            </FormLabel>
            <NumberInput isRequired step={1} defaultValue={0}>
              <NumberInputField id={"x"} {...register("x", { valueAsNumber: true })} color={color} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                Y
              </Text>
            </FormLabel>
            <NumberInput isRequired step={1} defaultValue={0}>
              <NumberInputField id={"y"} {...register("y", { valueAsNumber: true })} color={color} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </form>
      </Box>
    </BaseNode>
  );
};

export default memo(CoordinatesNode);
